import { ChangeDetectorRef, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { catchError, debounceTime, finalize, forkJoin, map, Observable, of, Subject, switchMap, takeUntil, tap } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ConfirmationService, MenuItem, MessageService, SelectItemGroup } from 'primeng/api';
import { AutoCompleteCompleteEvent } from 'primeng/autocomplete';
import _ from 'lodash';
import { BreadcrumbService, EnumObjectExtended, EnumValues, ModalService, Note, NotesSidebarComponent } from '@card-software/library';
import { GlobalMessageUtil } from '@card-software/nexus-utilities';
import { AuthService } from '@card-software/nexus-utilities';
import { Business, BusinessEntityType } from '@app/models/business';
import { LocationNode } from '@app/models/inventory/location';
import { PotentialWorkArea } from '@app/models/inventory/potential-work-area';
import {
  ItemMaster,
  ItemMasterGreigeSearchCommand,
  ItemRelationship,
  ItemRelationshipType,
  Uom,
} from '@app/models/item/item-master/item-master';
import { DyeingColor } from '@app/models/production/dyeing/dyeing-color';
import { KmInstructionsCommand } from '@app/models/production/km-instructions';
import { ProductionCard } from '@app/models/production/production-card';
import { ProductionCardEntryFormGroup, ProductionCardFormGroup } from '@app/models/production/production-card-forms';
import { WorkOrderType } from '@app/models/production/production-dashboard';
import { KnittingOrderRoles } from '@app/models/roles/roles-group';
import { MatchingItemMasterCommand } from '@app/models/yarn/yarn-search';
import { BusinessProxyService } from '@app/proxies/finance/business.proxy.service';
import { LocationProxyService } from '@app/proxies/inventory/location.proxy.service';
import { ItemMasterProxyService } from '@app/proxies/item/item-master.proxy.service';
import { LabDipRequestProxyService } from '@app/proxies/item/lab-dip-request-proxy.service';
import { KmInstructionsProxyService } from '@app/proxies/production/km-instructions.proxy.service';
import { ProductionCardsProxyService } from '@app/proxies/production/production-card.proxy.service';
import { ReportProxyService } from '@app/proxies/production/report.proxy.service';
import { RollProxyService } from '@app/proxies/production/roll.proxy.service';
import { LocationPathService } from '@app/services/location-path.service';
import { KnittingUtil } from '@app/utilities/knitting-util';
import { ProductionCardsSelectionComponent } from '@app/components/shared/_modals/production-cards-selection/production-cards-selection.component';
import { environment } from '../../../../environments/environment';
import { Asset } from '../../../models/asset/asset';
import { Provider } from '../../../models/finance/provider';
import { Greige } from '../../../models/greige/greige';
import { Yarn } from '../../../models/item/yarn';
import { Roll } from '../../../models/knitting/roll';
import { Fabric, WorkOrder, WorkOrderFabric, WorkOrderSearchCommand } from '../../../models/order/work-order';
import {
  FabricFormGroup,
  FabricQuantityDetail,
  FabricQuantityDetailsFormGroup,
  KnittingOrder,
  KnittingOrderStatus,
  YarnAndProviderInfoFormGroup,
} from '../../../models/production/knitting-order';
import { UserLite } from '../../../models/user-master/user-lite';
import { AssetProxyService } from '../../../proxies/asset/asset.proxy.service';
import { YarnProxyService } from '../../../proxies/item/yarn.proxy.service';
import { WorkOrderProxyService } from '../../../proxies/order/work-order.proxy.service';
import { KnittingOrderProxyService } from '../../../proxies/production/knitting-order.proxy.service';
import { EnumService } from '../../../services/enum.service';
import { KnittingMachineRolls, KnittingMachineRollsService } from '../../../services/knitting-machine-rolls.service';
import { UserAuthenticationService } from '../../../services/user-authentication.service';
import { FabricInformationModalComponent } from '../../shared/_modals/fabric-information-modal/fabric-information-modal.component';
import { RelationKnittingsModalComponent } from '../../shared/_modals/relation-knittings-modal/relation-knittings-modal.component';

@Component({
  selector: 'app-knitting-order',
  templateUrl: './knitting-order-details.component.html',
  styleUrl: './knitting-order-details.component.scss',
})
export class KnittingOrderDetailsComponent implements OnInit, OnDestroy {
  //#region General state
  originalKnittingOrder: KnittingOrder | null = null;
  userLite: UserLite | null;
  notes: Note[] = [];
  siblingKnittingOrders: KnittingOrder[] = [];
  globalUnitMeasure = 'lb';
  isLoading = signal(0);
  rolls: Roll[] = [];
  allColors: DyeingColor[] = [];
  invalidInputs = signal<boolean>(false);
  attempedToProgramMachines = false;
  saveMenuItems: MenuItem[] = [];
  showProgrammingPanel = signal<boolean>(false);
  greigeCodeItemMasterRecord: Record<string, ItemMaster> = {};
  fabricFormEnums: EnumValues;
  locations: LocationNode[] = [];
  public groupedFabricForms: FabricFormGroup[] = [];
  public fabricGroupOptions: { label: string; value: string }[] = [];
  displayModeControl = new FormControl<'grouped' | 'flat'>('grouped', { nonNullable: true });
  public viewModeOptions = [
    { label: 'Agrupado', value: 'grouped' },
    { label: 'Por Entrada', value: 'flat' },
  ];
  section: { label: string; value: number }[] = [];

  // Allowed work order types. Excludes: Linked, BGrade
  private readonly ALLOWED_WORK_ORDER_TYPES = [
    WorkOrderType.Sample,
    WorkOrderType.Bulk,
    WorkOrderType.GreigeStockForDyeing,
    WorkOrderType.ShortFall,
    WorkOrderType.GreigeOnly,
  ];

  // Allowed work order terms: CommissionKnitting(0), FullKnitting(1), FullPackage(3), CommissionKnittingAndDyeing(4). Excludes: Dyeing(2)
  private readonly ALLOWED_WORK_ORDER_TERMS = [0, 1, 3, 4];

  allAssets: Asset[] = [];
  filteredAssets: Asset[] = [];
  allGreiges: Greige[] = [];
  allYarns: Yarn[] = [];
  private currentCompatibilityFilter: { assetTypeReference: string; totalNeedles: number } | null = null;
  private machineSearch$ = new Subject<string>();
  private destroy$ = new Subject<void>();
  selectedCombos: KnittingMachineRolls[] = [];
  selectedComboKeys = new Set<string>();
  //#endregion

  //#region Production-card state
  productionCardsFormArray: FormArray<FormGroup<ProductionCardFormGroup>> = new FormArray<FormGroup<ProductionCardFormGroup>>([]);
  productionCardsEntriesFormArray: FormArray<FormGroup<ProductionCardEntryFormGroup>> = new FormArray<
    FormGroup<ProductionCardEntryFormGroup>
  >([]);
  productionCards: ProductionCard[] = [];
  productionCardsToDisplay: ProductionCard[] = [];
  allProductionCards: ProductionCard[] = [];
  productionCardsIds: string[] = [];
  //#endregion

  //#region Typeahead options
  assetOptions: Asset[] = [];
  greigeOptions: Greige[] = [];
  workOrderFabricOptions: Fabric[] = [];
  fabricVariantOptions: SelectItemGroup[] = [];
  //#endregion

  //#region Enums
  statusEnums: EnumValues = {};
  statusEnumObject?: EnumObjectExtended;
  workOrderTypes: EnumValues;
  workOrderTerms: EnumValues;
  //#endregion

  //#region Reactive forms & form controls
  status = new FormControl(0, [Validators.required]);
  location: FormControl<string | null> = new FormControl<string | null>(null, [Validators.required]);
  selectedSection = new FormControl(1);
  selectionControl = new FormControl<string[]>([], { nonNullable: true });
  selectAllControl = new FormControl<boolean>(false, { nonNullable: true });
  workOrderFabricSearch = new FormControl<Fabric | null>(null);
  public selectedFabricCodeControl = new FormControl<string | null>(null);
  productionCardsControl: FormControl<ProductionCard[] | null> = new FormControl(null);
  workOrder = new FormControl<WorkOrder | null>(null, [Validators.required]);
  fabricsQuantityDetails: FormArray<FormGroup<FabricQuantityDetailsFormGroup>> = new FormArray<FormGroup<FabricQuantityDetailsFormGroup>>(
    [],
  );
  asset: FormControl<Asset | null> = new FormControl<Asset | null>(null, [Validators.required]);
  greige: FormControl<Greige | null> = new FormControl<Greige | null>(null, [Validators.required]);
  addingNewComboForm: FormGroup = new FormGroup({
    asset: this.asset,
    greige: this.greige,
  });
  knittingOrderForm = new FormGroup({
    workOrder: this.workOrder,
    fabricsQuantityDetails: this.fabricsQuantityDetails,
  });
  machineProgrammingToolFormGroup = new FormGroup({
    assets: new FormControl<Asset[]>({ value: [], disabled: true }, [Validators.required, Validators.minLength(1)]),
    fabricAndVariantCode: new FormControl<string | null>(null, [Validators.required]),
    quantityBeToKnitting: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
  });
  yarnUniqueKeyAndItemMaster: Record<string, ItemMaster | null> = {};
  lbsPerRollControl = new FormControl<number>(60, { nonNullable: true, validators: [Validators.required] });

  //roles section
  private isEditor = signal<boolean>(false);
  private isViewer = signal<boolean>(false);
  blockSections = computed(() => this.isViewer());

  //#endregion

  //#region Injected services
  private activatedRoute = inject(ActivatedRoute);
  private assetProxyProxyService = inject(AssetProxyService);
  private breadcrumbService = inject(BreadcrumbService);
  private businessProxyService = inject(BusinessProxyService);
  private confirmationService = inject(ConfirmationService);
  private ctr = inject(ChangeDetectorRef);
  private enumService = inject(EnumService);
  private itemMasterProxyService = inject(ItemMasterProxyService);
  private kmInstructionsProxyService = inject(KmInstructionsProxyService);
  private knittingMachineRollsService = inject(KnittingMachineRollsService);
  private knittingOrderProxyService = inject(KnittingOrderProxyService);
  private labDipRequestProxyService = inject(LabDipRequestProxyService);
  private messageService = inject(MessageService);
  private modalService = inject(ModalService);
  private productionCardsProxyService = inject(ProductionCardsProxyService);
  private reportProxyService = inject(ReportProxyService);
  private rollProxyService = inject(RollProxyService);
  private router = inject(Router);
  private translateService = inject(TranslateService);
  private userAuthenticationService = inject(UserAuthenticationService);
  private workOrderProxyService = inject(WorkOrderProxyService);
  private yarnProxyService = inject(YarnProxyService);
  private locationProxyService = inject(LocationProxyService);
  private locationPathService = inject(LocationPathService);
  //#endregion

  constructor() {
    this.workOrderTypes = this.enumService.getEnum('WorkOrderType', 'orderEnums').values;
    this.workOrderTerms = this.enumService.getEnum('WorkOrderTerm', 'orderEnums').values;
    this.userLite = this.userAuthenticationService.userLite;
    this.fabricFormEnums = this.enumService.getEnum('FabricForm', 'itemEnums').values;
    this.statusEnumObject = this.enumService.getEnum('KnittingOrderStatus', 'productionEnums');
    this.statusEnums = this.statusEnumObject?.values ?? {};
    this.section = [
      {
        label: this.translateService.instant('Planning'),
        value: 1,
      },
      {
        label: this.translateService.instant('Programming'),
        value: 2,
      },
    ];

    this.saveMenuItems = [
      {
        label: this.translateService.instant('Save and Close'),
        icon: 'pi pi-save',
        command: () => this.saveKnittingOrder(true),
      },
    ];
  }

  //#region Lifecycle
  ngOnInit() {
    this.canEdit();
    this.setupMachineSearchDebounce();

    this.selectionControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((keys) => this.syncSelectionState(keys ?? []));

    this.selectAllControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((checked) => this.toggleSelectAll(checked ?? false));
    this.machineProgrammingToolFormGroup.get('fabricAndVariantCode')!.valueChanges.subscribe((value) => {
      if (value) {
        this.machineProgrammingToolFormGroup.get('quantityBeToKnitting')!.enable({ emitEvent: false });
        this.machineProgrammingToolFormGroup.get('assets')!.enable({ emitEvent: false });
      } else {
        this.machineProgrammingToolFormGroup.get('quantityBeToKnitting')!.disable({ emitEvent: false });
        this.machineProgrammingToolFormGroup.get('assets')!.disable({ emitEvent: false });
      }
    });
    this.groupFormGroupsByFabricCode();
    this.workOrderFabricSearch.valueChanges.subscribe((fabric) => {
      if (fabric?.id) {
        this.workOrderFabricSearch.setValue(null, { emitEvent: false });

        const workOrderFabric = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabric.fabricCode);

        if (!workOrderFabric) {
          GlobalMessageUtil.error(
            'Error',
            `${this.translateService.instant('knittingOrderDetails-RollsTable.Fabric')} ${fabric.fabricCode} ${this.translateService.instant('knittingOrderDetails_Messages.validFabric')}`,
          );
          return;
        }

        if (!this.canAddFabric(workOrderFabric)) {
          return;
        }

        this.fabricsQuantityDetails.push(this.createFormGroupFabric(workOrderFabric));
        this.groupFormGroupsByFabricCode();
      }
    });

    this.lbsPerRollControl.valueChanges.subscribe((value) => {
      this.knittingMachineRollsService.STANDARD_ROLL_WEIGHT = value > 0 ? value : 60;
    });

    this.loadLocations();

    this.activatedRoute.queryParams.subscribe(({ id }) => {
      // Limpiar FormArrays para evitar acumulación de datos al cambiar de ID
      this.productionCardsFormArray.clear();
      this.productionCardsEntriesFormArray.clear();
      this.fabricsQuantityDetails.clear();
      this.productionCardsIds = [];
      this.productionCards = [];

      if (id === 'new') {
        this.breadcrumbService.setCurrent('Common_New');
      }

      this.loadInitialData().subscribe((sucess) => {
        if (!sucess) {
          this.router.navigate(['/knitting-orders']);
          return;
        }
        if (id !== 'new') {
          this.incrementLoading();

          this.knittingOrderProxyService
            .getKnittingOrderById(id)
            .pipe(finalize(() => this.decrementLoading()))
            .subscribe({
              next: (results) => {
                if (_.isNil(results.data)) {
                  GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderNotFound'), 5000);
                  this.router.navigate(['/knitting-orders']);
                  return;
                }

                const knittingOrder = results.data;
                this.originalKnittingOrder = knittingOrder;
                this.locationPathService.load([knittingOrder.locationCode]);
                this.breadcrumbService.setCurrent(knittingOrder.knittingOrderNumber);
                this.status.setValue(knittingOrder.status ?? 0, { emitEvent: false });
                if (this.locations.length) {
                  const location = this.locations.find((loc) => loc.locationCode === knittingOrder.locationCode);
                  if (location) {
                    this.location.setValue(location.locationCode);
                  }
                }

                this.notes = knittingOrder.notes || [];

                const familyKnittingOrderNumber = KnittingUtil.getWorkOrderNumber(knittingOrder.knittingOrderNumber!);
                this.knittingOrderProxyService
                  .searchKnittingOrder({ knittingOrderNumber: familyKnittingOrderNumber }, 1, 25, false)
                  .subscribe({
                    next: (results) => {
                      const knittingOrders = results.data?.items || [];
                      this.siblingKnittingOrders = knittingOrders.filter((order) => order.id !== knittingOrder!.id);
                    },
                  });
                this.workOrderProxyService.getById(knittingOrder.workOrderReference!).subscribe({
                  next: ({ data }) => {
                    if (data) {
                      this.getProductionCards(data);
                      this.workOrder.setValue(data, { emitEvent: false });
                      this.setWorkOrderCertifications(data);
                      this.getRolls();
                      knittingOrder!.fabricQuantityDetails.forEach((fabric) => {
                        const workOrderFabric = data.fabrics.find((f) => f.fabricCode === fabric.fabricCode);
                        if (workOrderFabric) {
                          this.fabricsQuantityDetails.push(this.createFormGroupFabric(workOrderFabric, fabric));
                          this.groupFormGroupsByFabricCode();
                        }
                      });
                    }
                  },
                });
              },
              error: () => {
                this.router.navigate(['/knitting-order']);
              },
            });
        }
      });
    });

    this.workOrder.valueChanges.subscribe((workOrder) => {
      if (workOrder?.id) {
        this.setWorkOrderCertifications(workOrder);
        this.fabricsQuantityDetails.clear();

        // If it's a Greige work order (either type), auto-populate fabrics
        if (workOrder.orderType === WorkOrderType.GreigeStockForDyeing || workOrder.orderType === WorkOrderType.GreigeOnly) {
          this.autoPopulateFabrics(workOrder);
        }
      }
    });
    this.status.valueChanges.subscribe((value) => {
      const currentStatus = this.originalKnittingOrder?.status ?? 0;

      if (value === currentStatus) {
        return;
      }

      if (_.isNil(this.originalKnittingOrder?.id)) {
        return;
      }

      this.confirmationService.confirm({
        key: 'general-dialog',
        message: this.translateService.instant('Are you sure you want to change the status?'),
        accept: () => {
          this.updateKnittingOrderStatus(this.originalKnittingOrder!.id!, value ?? 0);
        },
        reject: () => {
          this.status.setValue(currentStatus, { emitEvent: false });
        },
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
    this.knittingMachineRollsService.clearData();
    this.productionCardsEntriesFormArray.clear();
  }

  private incrementLoading(): void {
    this.isLoading.update((v) => v + 1);
  }

  private decrementLoading(): void {
    this.isLoading.update((v) => Math.max(0, v - 1));
  }

  //#endregion

  //#region Getters & computed totals
  get headerTitle(): string {
    return _.isNil(this.originalKnittingOrder)
      ? this.translateService.instant('New_Knitting_Order')
      : this.translateService.instant('Knitting_Order') + ' ' + this.originalKnittingOrder.knittingOrderNumber;
  }

  get locationOptions(): { label: string; value: string }[] {
    return this.locations.map((location) => ({
      label: this.locationPathService.label(location.locationCode, location.name.en),
      value: location.locationCode,
    }));
  }

  public get knittingOrderStatusOptions(): EnumValues {
    if (!this.statusEnums || typeof this.statusEnums !== 'object') return {};

    const currentStatus = this.originalKnittingOrder?.status ?? 0;
    const filtered: EnumValues = {};

    if (_.isNil(this.originalKnittingOrder?.id)) {
      if (this.statusEnums[0]) {
        filtered[0] = this.statusEnums[0];
      }
      return filtered;
    }

    // Transiciones basadas en el estatus actual
    switch (currentStatus) {
      case 0: // Draft
        // Draft -> Submitted o Voided
        if (this.statusEnums[KnittingOrderStatus.Submitted])
          filtered[KnittingOrderStatus.Submitted] = this.statusEnums[KnittingOrderStatus.Submitted]; // Submitted
        if (this.statusEnums[KnittingOrderStatus.Voided])
          filtered[KnittingOrderStatus.Voided] = this.statusEnums[KnittingOrderStatus.Voided]; // Voided
        break;

      case 1: // Submitted
        // Submitted -> Voided o Closed
        if (this.statusEnums[KnittingOrderStatus.Approved])
          filtered[KnittingOrderStatus.Approved] = this.statusEnums[KnittingOrderStatus.Approved]; // Approved
        if (this.statusEnums[KnittingOrderStatus.Voided])
          filtered[KnittingOrderStatus.Voided] = this.statusEnums[KnittingOrderStatus.Voided]; // Voided
        break;

      case 2: // Approved
        // Approved -> Voided // Approved (mantener actual)
        if (this.statusEnums[KnittingOrderStatus.Voided])
          filtered[KnittingOrderStatus.Voided] = this.statusEnums[KnittingOrderStatus.Voided]; // Voided
        break;

      case 3: // InProgress
        // InProgress -> Closed o Voided (aunque no debería cambiarse manualmente desde UI)
        if (this.statusEnums[KnittingOrderStatus.InProgress])
          filtered[KnittingOrderStatus.InProgress] = this.statusEnums[KnittingOrderStatus.InProgress]; // InProgress (mantener actual)
        if (this.statusEnums[KnittingOrderStatus.Closed])
          filtered[KnittingOrderStatus.Closed] = this.statusEnums[KnittingOrderStatus.Closed]; // Closed
        if (this.statusEnums[KnittingOrderStatus.Voided])
          filtered[KnittingOrderStatus.Voided] = this.statusEnums[KnittingOrderStatus.Voided]; // Voided
        break;

      case 4: // Closed
        break;

      case 5: // Voided
        // Voided es un estado final, solo mostrar el actual
        break;

      default:
        // Por seguridad, si hay un estatus desconocido, mostrar solo el actual
        if (this.statusEnums[currentStatus]) {
          filtered[currentStatus] = this.statusEnums[currentStatus];
        }
        break;
    }

    return filtered;
  }

  get isGreigeStockForDyeingOrder(): boolean {
    return this.workOrder.value?.orderType === WorkOrderType.GreigeStockForDyeing;
  }

  get isGreigeOnlyOrder(): boolean {
    return this.workOrder.value?.orderType === WorkOrderType.GreigeOnly;
  }

  get isAnyGreigeOrder(): boolean {
    return this.isGreigeStockForDyeingOrder || this.isGreigeOnlyOrder;
  }

  groupFabricAsStrict(fabricGroup: unknown): FabricFormGroup {
    return fabricGroup as FabricFormGroup;
  }

  get knittingMachineCombos(): KnittingMachineRolls[] {
    const result = this.knittingMachineRollsService.getAllCombos();
    return result;
  }

  get printedRollCount(): number {
    let counter = 0;
    this.knittingMachineCombos.forEach((combo) => {
      counter += combo.savedRolls.filter((roll) => !_.isNil(roll.lastPrintedDate)).length;
    });
    return counter;
  }

  get totalRollsCount(): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      total += combo.savedRolls.length;
    });
    return total;
  }

  get totalIssuedWeight(): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      total += this.getIssuedRollsWeight(combo);
    });
    return total;
  }

  get totalIssuedRollsCount(): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      total += this.getIssuedRollsCount(combo);
    });
    return total;
  }

  get totalPlannedQuantityToKnit(): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      total += this.getQuantityToKnit(combo);
    });
    return total;
  }

  get totalRollCount(): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      total += combo.savedRolls.length;
    });
    return total;
  }

  getQuantityToKnit(combo: KnittingMachineRolls): number {
    return combo.savedRolls.reduce((total, roll) => {
      return total + (roll.plannedWeight ?? 0);
    }, 0);
  }

  getIssuedRollsCount(combo: KnittingMachineRolls): number {
    return combo.savedRolls.filter((roll) => !_.isEmpty(roll.stationTimeStamps) && roll.status !== 2).length;
  }

  getIssuedRollsWeight(combo: KnittingMachineRolls): number {
    const rollsWithWeight = combo.savedRolls.filter(
      (roll) => Array.isArray(roll.stationTimeStamps) && roll.stationTimeStamps.length > 0 && roll.status !== 2,
    );
    return rollsWithWeight.reduce((total, roll) => {
      const sortedTimestamps = KnittingUtil.getRollTimeStamp(roll);
      return total + (sortedTimestamps?.weightInLbs ?? 0);
    }, 0);
  }

  getFabricCodeIssuedQuantity(fabricCode: string, includeAllStatuses = false): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      combo.savedRolls.forEach((roll) => {
        if ((roll.originalFabricCode ?? roll.expectedFabricCode) === fabricCode) {
          if (!_.isNil(roll.stationTimeStamps) && !_.isEmpty(roll.stationTimeStamps) && (includeAllStatuses || roll.status !== 2)) {
            total += KnittingUtil.getRollTimeStamp(roll)?.weightInLbs ?? 0;
          }
        }
      });
    });
    return total;
  }

  getFabricCodeProgrammedQuantity(fabricCode: string): number {
    let total = 0;
    this.knittingMachineCombos.forEach((combo) => {
      combo.savedRolls.forEach((roll) => {
        if ((roll.originalFabricCode ?? roll.expectedFabricCode) === fabricCode) {
          if (_.isNil(roll.stationTimeStamps) || _.isEmpty(roll.stationTimeStamps)) {
            total += roll.plannedWeight;
          } else {
            total += roll.stationTimeStamps[0].weightInLbs!; // Assuming the first timestamp is the one we want
          }
        }
      });
    });
    return total;
  }

  getAssetByReference(reference: string): Asset | undefined {
    return this.allAssets.find((asset) => asset.id === reference);
  }

  getColorByReference(reference: string): DyeingColor | undefined {
    return this.allColors.find((color) => color.id === reference);
  }

  getLocationName(locationCode: string): string {
    // location.value holds the full locationCode (e.g. "NT.WA-KNITTING-1"), not the leaf code.
    const location = this.locations.find((loc) => loc.locationCode === locationCode);
    return this.locationPathService.label(locationCode, location?.name.en);
  }

  trackByFabricCode(index: number, group: { fabricCode: string }) {
    return group.fabricCode;
  }

  showEditingCombo(): boolean {
    return (
      !_.isNil(this.knittingMachineRollsService.getSelectedCombo()) &&
      !_.isNil(this.originalKnittingOrder) &&
      !_.isNil(this.originalKnittingOrder?.knittingOrderNumber)
    );
  }

  public getYarnAndProviderInfosControls(formGroup: FormGroup): AbstractControl[] {
    return (formGroup.get('yarnAndProviderInfos') as FormArray).controls;
  }

  //#endregion

  //#region Data loading
  loadInitialData(): Observable<true> {
    this.incrementLoading();
    return forkJoin({
      assets: this.assetProxyProxyService.filterAssets({
        assetId: 'KM',
      }),
      yarns: this.yarnProxyService.getAll(),
    }).pipe(
      tap(({ assets, yarns }) => {
        if (assets || yarns) {
          this.allAssets = _.filter(assets.data, (asset) => asset.assetId.startsWith('KM'));
          this.allYarns = yarns.data!;
        }
      }),
      finalize(() => this.decrementLoading()),
      catchError((error) => {
        console.error(error);
        return of(false);
      }),

      map(() => true),
    );
  }

  getRolls() {
    const knittingOrder = this.originalKnittingOrder;
    if (_.isNil(knittingOrder) && _.isNil(knittingOrder!.id)) {
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderNotFound'), 5000);
    }

    this.incrementLoading();
    this.rollProxyService
      .getRollsByKnittingOrder(knittingOrder!.id!, true)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          this.rolls = response.data || [];
          const fabrics = _.unionBy(this.workOrder.value?.fabrics || [], 'fabricCode').map((fabric) => fabric.fabric);
          this.knittingMachineRollsService.initializeData(this.allAssets, fabrics, this.rolls, knittingOrder!);
          this.applyWeighedRollsLock();
          if (!this.rolls || this.rolls.length === 0) {
            GlobalMessageUtil.warn(
              this.translateService.instant('noRolls'),
              this.translateService.instant('notRollsAssignToKnittingOrderYet'),
            );
          }
        },
        error: () => {
          const fabrics = _.unionBy(this.workOrder.value?.fabrics || [], 'fabricCode').map((fabric) => fabric.fabric);
          this.knittingMachineRollsService.initializeData(this.allAssets, fabrics, [], knittingOrder!);
          GlobalMessageUtil.warn(
            this.translateService.instant('noRolls'),
            this.translateService.instant('notRollsAssignToKnittingOrderYet'),
          );
        },
      });
  }

  getProductionCards(workOrder: WorkOrder) {
    this.incrementLoading();
    this.productionCardsProxyService
      .getProductionCardsByWorkOrderIdList(workOrder.id)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe((response) => {
        if (response.data && response.data.length > 0) {
          this.productionCards = _.filter(response.data, (pc) => {
            return this.originalKnittingOrder?.productionCardReferences?.includes(pc.id!);
          }) as ProductionCard[];
          this.fillProductionCardsFormArray(this.productionCards);
          this.fillProductionCardsEntriesOfKnittingOrder(this.productionCards);
        }
      });
  }

  getColors() {
    this.labDipRequestProxyService.getCustomerColors(this.workOrder.value!.customerReference!).subscribe({
      next: (colors) => {
        this.allColors = colors.data!;
      },
    });
  }

  private loadLocations() {
    this.incrementLoading();
    const locationCodes = [PotentialWorkArea.NTWorkArea, PotentialWorkArea.SIMTEXWorkArea, PotentialWorkArea.GUAKWorkArea];
    this.locationPathService.load(locationCodes);
    this.locationProxyService
      .batchGetByCodes(locationCodes)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.locations = response.data || [];
            const knittingOrder = this.originalKnittingOrder;
            if (knittingOrder && this.locations.length) {
              const location = this.locations.find((loc) => loc.locationCode === knittingOrder.locationCode);
              if (location) {
                this.location.setValue(location.locationCode);
              }
            }
          }
        },
        error: () => {
          this.locations = [];
        },
      });
  }

  getKnittingOrderReport() {
    this.reportProxyService.getKnittingOrderReport(this.originalKnittingOrder!.id!).subscribe({
      next: (response) => {
        const blob = new Blob([response], { type: 'application/pdf' });
        const downloadUrl = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = `KnittingOrder-${this.originalKnittingOrder?.knittingOrderNumber}.pdf`;
        link.click();
        window.URL.revokeObjectURL(downloadUrl);
        this.messageService.add({
          severity: 'success',
          summary: 'Éxito',
          detail: 'The report has been downloaded successfully!',
          key: 'br',
        });
      },
    });
  }

  private setWorkOrderCertifications(workOrder: WorkOrder) {
    workOrder.fabrics.forEach((fabric) => {
      const command: MatchingItemMasterCommand[] = [];
      fabric.fabric?.greige?.yarnDetails.forEach((yd) => {
        const certifications =
          fabric.yarnCertificationDetails?.find((cert) => cert.yarnCode === yd.yarnCode)?.certificationReferences ?? [];
        const yarnCode = yd.yarnCode;
        command.push({
          yarnCode: yarnCode!,
          certificationReference: certifications,
        });
      });
      this.getItemMasters(command, workOrder.id, fabric.fabricCode);
    });
  }

  //#endregion

  //#region Typeaheads
  assetOptionsTypeAhead(event: AutoCompleteCompleteEvent): void {
    const term = event.query.toLowerCase();
    if (term === '') {
      this.assetOptions = this.allAssets;
    }
    this.assetOptions = _.sortBy(
      this.allAssets.filter((asset) => asset.assetId.toLowerCase().includes(term)),
      (asset) => asset.assetId.toLowerCase().indexOf(term),
    );
  }

  public fetchManufacturers = (query: string): Observable<Business[]> => {
    const searchCommand = { name: query, businessTypes: [BusinessEntityType.Manufacturer] };

    return this.businessProxyService.search(searchCommand).pipe(
      map((manufacturers) =>
        (manufacturers.data?.items ?? []).map((manufacturer) => ({
          ...manufacturer,
          fieldToDisplay: manufacturer.code + ' - ' + manufacturer.name,
        })),
      ),
    );
  };

  public fetchWorkOrders = (query: string): Observable<WorkOrder[]> => {
    const term = query.toLowerCase();

    const workCommand: WorkOrderSearchCommand = {
      orderNumber: term,
    };

    return this.workOrderProxyService.search(workCommand, 1, 50).pipe(
      // Filter by allowed types and terms in the UI
      map(({ data }) =>
        (data?.items ?? []).filter(
          (workOrder) =>
            workOrder.orderNumber.toLowerCase().includes(term) &&
            this.ALLOWED_WORK_ORDER_TYPES.includes(workOrder.orderType) &&
            this.ALLOWED_WORK_ORDER_TERMS.includes(workOrder.workOrderTerm),
        ),
      ),
    );
  };

  async workOrderFabricTypeahead(event: AutoCompleteCompleteEvent) {
    const term = event.query.toLowerCase();
    if (this.workOrder.value?.fabrics) {
      this.workOrderFabricOptions = this.workOrder.value.fabrics
        .filter((fabric) => fabric.fabricCode.toLowerCase().includes(term))
        .map((fabric) => {
          return { ...fabric.fabric, fieldToDisplay: fabric.fabric.fabricCode + ' - ' + fabric.fabric.longDescription };
        });
    }
  }

  async fabricVariantTypeahead() {
    const fabricDetails = this.fabricsQuantityDetails.controls.map((formGroup) => formGroup.value);

    const options = fabricDetails.map((fabric) => {
      return {
        fieldToDisplay: fabric!.fabric!.fabricCode + ' - ' + fabric!.fabric!.longDescription,
        fabricCode: fabric.fabric!.fabricCode,
        variantCode: fabric.variantCode!,
        quantity: fabric.quantity ?? 0,
        unitOfMeasure: fabric.unitOfMeasure ?? 0,
        yarnAndProviderInfos: [],
      };
    });

    const groupByFabricCode = _.groupBy(options, 'fabricCode');
    const keys = Object.keys(groupByFabricCode);
    this.fabricVariantOptions = keys.map((key) => {
      const fabricDetails = this.fabricsQuantityDetails.controls.find((formGroup) => formGroup.value!.fabric!.fabricCode === key)?.value;
      const fabricQuantityDetails = groupByFabricCode[key];
      return {
        label: fabricDetails!.fabric!.fabricCode + ' - ' + fabricDetails!.fabric!.longDescription,
        value: fabricQuantityDetails,
        items: fabricQuantityDetails.map((fabric) => ({
          label: fabric.variantCode,
          value: fabric.fabricCode + '-' + fabric.variantCode,
        })),
      };
    });
  }

  searchProductionCards(event: AutoCompleteCompleteEvent, fabricCode: string) {
    let productionCards: ProductionCard[] = [];
    if (this.originalKnittingOrder) {
      productionCards = this.allProductionCards.filter(
        (x) =>
          x.productionCardNumber?.toLowerCase().includes(event.query.toLowerCase()) &&
          x.productionCardEntries.some((entry) => entry.fabricCode === fabricCode) &&
          this.originalKnittingOrder?.productionCardReferences?.includes(x.id!),
      );
    } else {
      productionCards = this.allProductionCards.filter(
        (x) =>
          x.productionCardNumber?.toLowerCase().includes(event.query.toLowerCase()) &&
          x.productionCardEntries.some((entry) => entry.fabricCode === fabricCode) &&
          this.productionCardsIds.includes(x.id!),
      );
    }

    if (productionCards.length > 8) {
      productionCards = productionCards.slice(0, 8);
    }
    this.productionCardsToDisplay = productionCards;
  }

  //#endregion

  //#region Machine programming
  programMachinesWithTool() {
    this.attempedToProgramMachines = true;

    if (this.machineProgrammingToolFormGroup.invalid) {
      GlobalMessageUtil.warn(
        'Please fill all required fields',
        'There are some required fields that need to be filled before programming the machines.',
      );
      return;
    }

    this.attempedToProgramMachines = false;
    const assets = this.machineProgrammingToolFormGroup.value.assets!;
    const fabricAndVariantCode = this.machineProgrammingToolFormGroup.value.fabricAndVariantCode!;
    const fabricCodeAndVariantKey = KnittingUtil.separateFabricCodeAndVariantCode(fabricAndVariantCode);
    const variantCode = fabricCodeAndVariantKey.variantCode;
    const fabricCode = fabricCodeAndVariantKey.fabricCode;
    const greigeCode = this.workOrder.value?.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.fabric?.greige?.greigeCode;
    if (!greigeCode) {
      GlobalMessageUtil.error('Error', 'Greige code not found for the selected fabric. Please try again, or contact support.');
      return;
    }
    const itemMaster = this.greigeCodeItemMasterRecord[greigeCode];
    if (!itemMaster) {
      GlobalMessageUtil.error('Error', 'Greige Item Master not found for the selected fabric. Please ask Master Data team to create it.');
      return;
    }

    this.incrementLoading();
    this.knittingMachineRollsService
      .batchCreateRolls(
        assets,
        variantCode,
        fabricCode,
        this.machineProgrammingToolFormGroup.value.quantityBeToKnitting!,
        itemMaster.itemCode,
      )
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe((result) => {
        if (!_.isEmpty(result)) {
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('CommonMessages.Success'),
            detail: `${this.translateService.instant('knittingOrderDetails_Messages.created')} ${result.length} ${this.translateService.instant('knittingOrderDetails_Messages.rolls')}`,
            key: 'br',
          });
          this.closeProgrammingTool();
        }
      });
  }

  closeProgrammingTool() {
    this.showProgrammingPanel.set(false);
    this.machineProgrammingToolFormGroup.reset();
  }

  filteredAssetByFabricSelection() {
    this.filteredAssets = [];
    this.currentCompatibilityFilter = null;
    this.machineProgrammingToolFormGroup.controls.assets.setValue([]);

    const fabricDetail = this.fabricsQuantityDetails.controls.find(
      (formGroup) =>
        this.machineProgrammingToolFormGroup.value.fabricAndVariantCode ===
        formGroup.value.fabric!.fabricCode + '-' + formGroup.value.variantCode,
    );

    if (!fabricDetail) {
      console.warn('No fabric detail found for the selected fabric and variant code');
      return;
    }

    this.getFabricGreigeItemMaster(fabricDetail);

    const machineInfo = fabricDetail.value.fabric?.greige.machineInformation?.knittingMachineDetailsSnapshot;
    const totalNeedles = (machineInfo?.cylinderNeedles ?? 0) + (machineInfo?.dialNeedles ?? 0);
    const assetId = fabricDetail.value.fabric?.greige.machineInformation?.assetId;

    // Resolve assetTypeReference from already-loaded allAssets (no extra API call needed)
    const referenceAsset = this.allAssets.find((a) => a.assetId === assetId);
    if (!referenceAsset?.assetTypeReference) return;

    this.currentCompatibilityFilter = { assetTypeReference: referenceAsset.assetTypeReference, totalNeedles };

    this.assetProxyProxyService
      .filterAssets({
        assetTypeReference: referenceAsset.assetTypeReference,
        totalNeedles: [totalNeedles],
      })
      .subscribe({
        next: (filtered) => {
          const results = filtered.data ?? [];
          if (results.length === 0) {
            GlobalMessageUtil.error('Error', 'No asset found with the required needles');
          }
          this.filteredAssets = results;
        },
      });
  }

  private setupMachineSearchDebounce(): void {
    this.machineSearch$
      .pipe(
        debounceTime(300),
        switchMap((searchText) => {
          if (!this.currentCompatibilityFilter) return of(null);
          return this.assetProxyProxyService.filterAssets({
            assetId: searchText || undefined,
            assetTypeReference: this.currentCompatibilityFilter.assetTypeReference,
            totalNeedles: [this.currentCompatibilityFilter.totalNeedles],
          });
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (res) => {
          if (!res) return;
          const results = res.data ?? [];
          const currentSelected: Asset[] = this.machineProgrammingToolFormGroup.controls.assets.value ?? [];

          this.filteredAssets = _.uniqBy([...currentSelected, ...results], 'assetId');
        },
      });
  }

  onMachineFilterChange(event: { filter: string }): void {
    this.machineSearch$.next(event.filter ?? '');
  }

  filterAssetsBySumDialCylindersNeedles(assets: Asset[], needlesRequired: number): void {
    const filteredAssets = assets.filter((asset) => {
      const machineInfo = asset.knittingMachineDetails;
      return (machineInfo?.cylinderNeedles ?? 0) + (machineInfo?.dialNeedles ?? 0) === needlesRequired;
    });
    if (filteredAssets.length <= 0) {
      GlobalMessageUtil.error('Error', 'No asset found with the required needles');
      this.filteredAssets = [];
    }

    this.filteredAssets = filteredAssets;
  }

  private getFabricGreigeItemMaster(fabricFormGroup: FormGroup<FabricQuantityDetailsFormGroup>) {
    const fabricCode = fabricFormGroup?.value.fabric?.fabricCode;
    const workOrderId = this.workOrder.value?.id;
    const greigeCode = fabricFormGroup?.value.fabric?.greige?.greigeCode;

    if (!fabricCode || !workOrderId || !greigeCode) {
      console.error('Fabric code, work order ID, or greige code is missing');
      return;
    }

    if (this.greigeCodeItemMasterRecord[greigeCode]) {
      return;
    }
    const relationships: ItemRelationship[] =
      fabricFormGroup?.value.yarnAndProviderInfos
        ?.map((yarnAndProviderInfo) => {
          // Ensure yarn and workOrderId and fabricCode are defined
          if (!yarnAndProviderInfo?.yarn || !workOrderId || !fabricCode) {
            return null;
          }
          const itemCode = this.getYarnCodeItemMasterCode(this.buildUniqueKey(yarnAndProviderInfo.yarn.code, workOrderId, fabricCode));
          const relationship: ItemRelationship = {
            itemMasterCode: itemCode,
            quantity: this.getYarnPercentage(yarnAndProviderInfo.yarn.code, fabricFormGroup.value.fabric!),
            relationshipType: ItemRelationshipType.Composition,
            unitOfMeasure: Uom.pct,
          };
          return relationship;
        })
        .filter((relationship): relationship is ItemRelationship => relationship !== null) || [];

    if (_.isEmpty(relationships)) {
      console.error('No yarns found for fabric', fabricFormGroup.value.fabric?.fabricCode);
      return;
    }

    if (relationships.length !== fabricFormGroup?.value.yarnAndProviderInfos?.length) {
      console.error('Mismatch in yarns and relationships length for fabric', fabricFormGroup.value.fabric?.fabricCode);
      return;
    }

    const command: ItemMasterGreigeSearchCommand = {
      baseGreigeCode: greigeCode,
      itemMasterRelationships: relationships,
    };

    this.itemMasterProxyService.getItemMastersForGreigeCodes([command]).subscribe({
      next: (response) => {
        if (response.success && !_.isNil(response.data)) {
          const itemMaster = response.data[greigeCode];
          if (!itemMaster) {
            GlobalMessageUtil.error(
              'Greige Item Master Not Found',
              'No item master found for the provided greige code. Please ask Master Data team to create it.',
            );
            return;
          }
          this.greigeCodeItemMasterRecord[greigeCode] = itemMaster;
        }
      },
    });
  }

  //#endregion

  //#region FormGroup creation & production cards
  createFormGroupFabric(
    fabric: WorkOrderFabric,
    knittingOrderFabric: FabricQuantityDetail | null = null,
  ): FormGroup<FabricQuantityDetailsFormGroup> {
    const formGroup = new FormGroup<FabricQuantityDetailsFormGroup>({
      fabric: new FormControl<Fabric>(fabric.fabric!, {
        nonNullable: true,
      }),
      variantCode: new FormControl<string>(
        !_.isNil(knittingOrderFabric?.variantCode)
          ? knittingOrderFabric.variantCode
          : KnittingUtil.getNextVariantCode(fabric.fabricCode, this.fabricsQuantityDetails.controls),
        {
          nonNullable: true,
        },
      ),
      quantity: new FormControl<number>(!_.isNil(knittingOrderFabric) ? knittingOrderFabric.quantity : 0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0.01)],
      }),
      unitOfMeasure: new FormControl<number>(this.workOrder.value?.uom ?? 0, {
        nonNullable: true,
      }),
      productionCardReference: new FormControl<ProductionCard | null>(
        this.allProductionCards.find((pc) => pc.id == knittingOrderFabric?.productionCardReference) ?? null,
      ),
      yarnAndProviderInfos: new FormArray<FormGroup<YarnAndProviderInfoFormGroup>>(
        // Group yarn details by yarnCode to avoid duplicates - only create one form control per unique yarn code
        _.uniqBy(fabric.fabric.greige.yarnDetails, 'yarnCode').map((yarnDetails) => {
          const yarn = this.allYarns.find((yarn) => yarn.code === yarnDetails.yarnCode);
          const knittingOrderYarn = knittingOrderFabric?.yarnAndProviderInfos.find(
            (yarnAndProviderInfo) => yarnAndProviderInfo.yarnCode === yarnDetails.yarnCode,
          );
          if (!yarn) {
            console.warn('Yarn not found for yarnCode:', yarnDetails.yarnCode);
          }
          return new FormGroup<YarnAndProviderInfoFormGroup>({
            yarn: new FormControl<Yarn>(yarn!, {
              nonNullable: true,
              validators: [Validators.required],
            }),
            provider: new FormControl<Provider | null>(
              !_.isNil(knittingOrderYarn?.provider)
                ? {
                    ...knittingOrderYarn?.provider,
                    fieldToDisplay: knittingOrderYarn?.provider?.code + ' - ' + knittingOrderYarn?.provider?.name,
                  }
                : null,
              {
                nonNullable: true,
              },
            ),
            providerLotNumber: new FormControl<string>(
              !_.isNil(knittingOrderYarn?.providerLotNumber) ? knittingOrderYarn.providerLotNumber : '',
              {
                nonNullable: true,
              },
            ),
          });
        }),
      ),
    });
    return formGroup;
  }

  private autoPopulateFabrics(workOrder: WorkOrder): void {
    // For Greige work orders, automatically populate fabrics from work order
    workOrder.fabrics.forEach((workOrderFabric) => {
      const fabricFormGroup = this.createFormGroupFabric(workOrderFabric);

      const estimatedQuantity = this.getGreigeWorkOrderEstimatedWithKnittingLoss(workOrderFabric.fabricCode);
      fabricFormGroup.controls.quantity.setValue(estimatedQuantity);

      this.fabricsQuantityDetails.push(fabricFormGroup);
    });

    this.groupFormGroupsByFabricCode();
  }

  private groupFormGroupsByFabricCode(): void {
    const map = new Map<string, FormGroup[]>();
    this.fabricsQuantityDetails.controls.forEach((fg: FormGroup) => {
      const code = fg.value.fabric?.fabricCode ?? '';
      if (!map.has(code)) map.set(code, []);
      map.get(code)!.push(fg);
    });

    this.groupedFabricForms = Array.from(map.entries()).map(([fabricCode, groups]) => ({
      fabricCode,
      groups,
      variantControl: new FormControl(0, { nonNullable: true }),
      variantOptions: groups.map((g, i) => ({
        label: g.value.variantCode,
        value: i,
      })),
    }));

    const newOptions = this.groupedFabricForms.map((group) => ({
      label: group.fabricCode,
      value: group.fabricCode,
    }));

    if (JSON.stringify(this.fabricGroupOptions) !== JSON.stringify(newOptions)) {
      this.fabricGroupOptions = newOptions;
    }

    const currentValue = this.selectedFabricCodeControl.value;
    const validCodes = this.groupedFabricForms.map((g) => g.fabricCode);

    if (!currentValue || !validCodes.includes(currentValue)) {
      const firstValid = validCodes[0] ?? null;
      this.selectedFabricCodeControl.setValue(firstValid, { emitEvent: false });
    }
  }

  public deleteFabricGroupGroup(groupIndex: number): void {
    const group = this.groupedFabricForms[groupIndex];
    group.groups.forEach((fg) => {
      const idx = this.fabricsQuantityDetails.controls.indexOf(fg);
      if (idx > -1) this.fabricsQuantityDetails.removeAt(idx);
    });
    this.groupFormGroupsByFabricCode();
  }

  public addVariantToGroup(fabricCode: string): void {
    const workOrder = this.workOrder.value;
    if (!workOrder) {
      return;
    }

    const woFabric = workOrder.fabrics.find((wf) => wf.fabric.fabricCode === fabricCode);
    if (!woFabric) {
      return;
    }

    if (!this.canAddFabric(woFabric)) {
      return;
    }

    const newGroup = this.createFormGroupFabric(woFabric);

    this.fabricsQuantityDetails.push(newGroup);
    this.groupFormGroupsByFabricCode();
  }

  public deleteVariant(fabricCode: string, variantIndex: number): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('Danger Zone'),
      message: this.translateService.instant('Are you sure you want to delete this variant?'),
      acceptLabel: this.translateService.instant('Confirm'),
      rejectLabel: this.translateService.instant('Cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-secondary',
      key: 'general-dialog',
      accept: () => {
        const variants = this.fabricsQuantityDetails.controls.filter((fg) => fg.value.fabric?.fabricCode === fabricCode);

        if (variantIndex >= variants.length) {
          return;
        }

        const actualIndex = this.fabricsQuantityDetails.controls.indexOf(variants[variantIndex]);

        if (actualIndex !== -1) {
          this.fabricsQuantityDetails.removeAt(actualIndex);

          this.groupFormGroupsByFabricCode();
        }
      },
      reject: () => {
        return;
      },
    });
  }

  viewProductionCardsModal(): void {
    this.modalService
      .open(
        ProductionCardsSelectionComponent,
        { modalClass: 'modal_large' },
        {
          workOrder: this.workOrder.value,
          currentProductionCards: this.productionCardsFormArray,
          previouslySelectedProductionCards: this.productionCards,
        },
      )
      .subscribe((res) => {
        if (!res || !('data' in res) || !res.data) return;
        res.data.controls.forEach((group) => {
          this.productionCardsFormArray.controls.push(group);
        });

        this.fillProductionCardsEntries(res.data);
      });
  }

  fillProductionCardsEntries(productionCards: FormArray<FormGroup<ProductionCardFormGroup>>) {
    const newQuantityMap = new Map<string, number>();

    productionCards.controls.forEach((pc) => {
      const productionCardId = pc.get('id')?.value;
      if (productionCardId) {
        this.productionCardsIds.push(productionCardId);
      }
    });

    productionCards.controls.forEach((pc) => {
      const entries = pc.get('productionCardEntries') as FormArray<FormGroup<ProductionCardEntryFormGroup>>;
      entries.controls.forEach((entry) => {
        const fabricCode = entry.get('fabricCode')?.value ?? '';
        const quantity = entry.get('quantity')?.value ?? 0;
        if (!fabricCode) return;

        newQuantityMap.set(fabricCode, (newQuantityMap.get(fabricCode) || 0) + quantity);
      });
    });

    this.productionCardsEntriesFormArray.controls.forEach((control) => {
      const fabricCode = control.get('fabricCode')?.value;
      if (fabricCode && newQuantityMap.has(fabricCode)) {
        const existingQty = control.get('quantity')?.value || 0;
        const addedQty = newQuantityMap.get(fabricCode) || 0;
        control.get('quantity')?.setValue(existingQty + addedQty);

        newQuantityMap.delete(fabricCode);
      }
    });

    newQuantityMap.forEach((quantity, fabricCode) => {
      const newForm = new FormGroup<ProductionCardEntryFormGroup>({
        fabricCode: new FormControl<string | null>(fabricCode),
        quantity: new FormControl<number | null>(quantity),
      });
      this.productionCardsEntriesFormArray.push(newForm);
    });
  }

  fillProductionCardsEntriesOfKnittingOrder(productionCards: ProductionCard[]) {
    const quantityMap = new Map<string, number>();

    productionCards.forEach((pc) => {
      pc.productionCardEntries?.forEach((entry) => {
        const current = quantityMap.get(entry.fabricCode) || 0;
        quantityMap.set(entry.fabricCode, current + entry.quantity);
      });
    });

    quantityMap.forEach((quantity, fabricCode) => {
      this.productionCardsEntriesFormArray.push(
        new FormGroup({
          fabricCode: new FormControl(fabricCode),
          quantity: new FormControl(quantity),
        }),
      );
    });
  }

  fillProductionCardsFormArray(productionCards: ProductionCard[]) {
    this.productionCardsFormArray.clear();

    for (const card of productionCards) {
      const formGroup = new FormGroup<ProductionCardFormGroup>({
        id: new FormControl<string | null>(card.id ?? null),
        productionCardNumber: new FormControl<string | null>(card.productionCardNumber ?? null),
        color: new FormControl<DyeingColor | null>(this.getColorByReference('') ?? null),
        colorName: new FormControl<string | null>(card.colorName ?? null),
        dyeingAsset: new FormControl<Asset | null>(this.getAssetByReference('') ?? null),
        status: new FormControl<number | null>(card.status ?? null),
        productionCardEntries: new FormArray<FormGroup<ProductionCardEntryFormGroup>>([]),
        isNew: new FormControl<boolean | null>(false),
        batchingEstimatedDate: new FormControl<Date | null>(card.batchingEstimatedDate ?? null),
        dyeingEstimatedStartDate: new FormControl<Date | null>(card.dyeingEstimatedStartDate ?? null),
        finishingEstimatedEndDate: new FormControl<Date | null>(card.finishingEstimatedEndDate ?? null),
        rollNumbers: new FormControl<string[]>(card.rollNumbers ?? [], { nonNullable: true }),
      });

      this.productionCardsFormArray.push(formGroup);
    }
  }

  //#endregion

  //#region Combo selection
  comboKey(combo: KnittingMachineRolls): string {
    return KnittingUtil.getComboKey(combo.asset.assetId, combo.fabric.fabricCode, combo.variantCode);
  }

  toggleSelectAll(checked: boolean): void {
    const keys = checked ? this.knittingMachineCombos.map((combo) => this.comboKey(combo)) : [];
    this.selectionControl.setValue(keys);
  }

  private syncSelectionState(keys: string[]): void {
    this.selectedComboKeys = new Set(keys);
    this.selectedCombos = this.knittingMachineCombos.filter((combo) => this.selectedComboKeys.has(this.comboKey(combo)));

    const allSelected = this.knittingMachineCombos.length > 0 && keys.length === this.knittingMachineCombos.length;
    if (this.selectAllControl.value !== allSelected) {
      this.selectAllControl.setValue(allSelected, { emitEvent: false });
    }

    this.ctr.detectChanges();
  }

  clearAllCombos() {
    this.selectionControl.setValue([], { emitEvent: false });
    this.selectAllControl.setValue(false, { emitEvent: false });
    this.selectedComboKeys = new Set();
    this.selectedCombos = [];
    this.knittingMachineRollsService.clearData();
    this.ctr.detectChanges();
    this.getRolls();
  }

  //#endregion

  //#region Visual validators & quantity calculations
  canAddFabric(workOrderFabric: WorkOrderFabric): boolean {
    const allControls = this.fabricsQuantityDetails.controls.filter((formGroup) => {
      const fabricControl = formGroup.controls.fabric;
      return fabricControl.value.fabricCode === workOrderFabric.fabricCode;
    });

    //if there are controls that have yarns with no provider or no provider lot number we CANNOT add the fabric
    const hasInvalidYarnAndProviderInfos = allControls.some((formGroup) => {
      const yarnAndProviderInfos = formGroup.controls.yarnAndProviderInfos.controls;
      return yarnAndProviderInfos.some((yarnControl) => {
        const providerControl = yarnControl.controls.provider;
        const providerLotNumberControl = yarnControl.controls.providerLotNumber;
        return !providerControl.value || !providerLotNumberControl.value;
      });
    });

    if (hasInvalidYarnAndProviderInfos) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        life: 5000,
        detail: `${this.translateService.instant('knittingPleaseFillProvider')} (${workOrderFabric.fabricCode}) ${
          workOrderFabric.fabric.longDescription
        }`,
        key: 'br',
      });
      return false;
    }

    return true;
  }

  hasWeighedRolls(fabricCode: string, variantCode: string): boolean {
    return this.knittingMachineCombos.some((combo) =>
      combo.savedRolls.some(
        (roll) =>
          (roll.originalFabricCode ?? roll.expectedFabricCode) === fabricCode &&
          roll.knittingOrderVariantCode === variantCode &&
          !_.isNil(roll.stationTimeStamps) &&
          !_.isEmpty(roll.stationTimeStamps),
      ),
    );
  }

  applyWeighedRollsLock(): void {
    this.fabricsQuantityDetails.controls.forEach((fabricControl) => {
      const fabricCode = fabricControl.value.fabric?.fabricCode ?? '';
      const variantCode = fabricControl.value.variantCode ?? '';
      const locked = this.hasWeighedRolls(fabricCode, variantCode);
      fabricControl.controls.yarnAndProviderInfos.controls.forEach((yarnControl) => {
        if (locked) {
          yarnControl.controls.provider.disable({ emitEvent: false });
          yarnControl.controls.providerLotNumber.disable({ emitEvent: false });
        } else {
          yarnControl.controls.provider.enable({ emitEvent: false });
          yarnControl.controls.providerLotNumber.enable({ emitEvent: false });
        }
      });
    });
  }

  private hasInvalidFabricQuantities(): boolean {
    return this.fabricsQuantityDetails.controls.some((ctrl) => {
      const quantity = ctrl.get('quantity')?.value;
      return !quantity || quantity <= 0;
    });
  }

  getInvalidControls(form: FormGroup | FormArray): string[] {
    const invalidControls: string[] = [];

    function traverse(control: AbstractControl, path: string) {
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach((key) => {
          traverse(control.controls[key], path ? `${path}.${key}` : key);
        });
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl, index) => {
          traverse(ctrl, `${path}[${index}]`);
        });
      } else if (control.invalid) {
        invalidControls.push(path);
      }
    }

    traverse(form, '');
    return invalidControls;
  }

  getActualPlannedEstimatedQuantity(fabricCode: string): number {
    if (this.isAnyGreigeOrder) {
      return this.workOrder.value?.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.quantityInUOM ?? 0;
    }
    let value = 0;
    value = this.workOrder.value?.fabrics.find((fabric) => fabric.fabricCode === fabricCode)?.quantities?.actualPlannedQuantity ?? 0;

    return value > 0 ? Math.round(value / 0.99) : 0;
  }

  getEstimatedKnittingPlanningQuantity(fabricCode: string): number {
    const value = this.getActualPlannedEstimatedQuantity(fabricCode);
    return value > 0 ? Math.round(value / 0.99) : 0;
  }

  getPreviouslyPlannedQuantity(fabricCode: string): number {
    //check sibling knittingo orders and add up the quantities
    return (
      this.siblingKnittingOrders
        .map((order) => order.fabricQuantityDetails.find((fabric) => fabric.fabricCode === fabricCode)?.quantity)
        .reduce((total, quantity) => (total ?? 0) + (quantity ?? 0), 0) || 0
    );
  }

  getCurrentPlannedQuantity(fabricCode: string): number {
    return this.fabricsQuantityDetails.controls
      .filter((formGroup) => formGroup.controls.fabric.value.fabricCode === fabricCode)
      .reduce((total, formGroup) => total + formGroup.controls.quantity.value, 0);
  }

  getMissingQuantity(fabricCode: string): number {
    const estimatedKnittingPlannedQty = this.getEstimatedKnittingPlanningQuantity(fabricCode);
    const currentPlannedQty = this.getCurrentPlannedQuantity(fabricCode) + this.getPreviouslyPlannedQuantity(fabricCode);
    return estimatedKnittingPlannedQty - currentPlannedQty;
  }

  public getEstimatedKnittingQuantity(group: FormGroup<ProductionCardEntryFormGroup>) {
    const fabric = this.workOrder.value!.fabrics.find((f) => f.fabricCode === group.controls.fabricCode.value);
    let lossPercentage = 1;
    if (fabric) {
      lossPercentage =
        !_.isNil(fabric?.fabric?.greige.knittingProcessLossPercentage) && !_.isNaN(fabric?.fabric?.greige.knittingProcessLossPercentage)
          ? fabric?.fabric?.greige.knittingProcessLossPercentage
          : 1;
    }
    const quantity = group?.controls?.quantity?.value;
    if (quantity == null) {
      return 0;
    }
    return Math.round(quantity / (1 - lossPercentage / 100));
  }

  public getGreigeWorkOrderEstimatedWithDyeingLoss(fabricCode: string): number {
    const fabric = this.workOrder.value!.fabrics.find((f) => f.fabricCode === fabricCode);
    const lossPercentage = fabric?.fabric.finishingDetails?.dyeingAndFinishingLossPercentage ?? 0;
    const quantity = fabric?.quantityInPounds ?? 0;

    if (lossPercentage > 0) {
      return Math.round(quantity / (1 - lossPercentage / 100));
    }
    return quantity;
  }

  public getGreigeWorkOrderEstimatedWithKnittingLoss(fabricCode: string): number {
    const fabric = this.workOrder.value!.fabrics.find((f) => f.fabricCode === fabricCode);
    const knittingLossPercentage = fabric?.fabric?.greige.knittingProcessLossPercentage ?? 0;

    // For GreigeOnly orders, only apply knitting loss (skip dyeing loss)
    // For GreigeStockForDyeing orders, apply both dyeing and knitting loss
    const baseQuantity = this.isGreigeOnlyOrder
      ? (fabric?.quantityInPounds ?? 0)
      : this.getGreigeWorkOrderEstimatedWithDyeingLoss(fabricCode);

    if (knittingLossPercentage > 0) {
      return Math.round(baseQuantity / (1 - knittingLossPercentage / 100));
    }
    return baseQuantity;
  }

  public getFabricLoopLengths(fabricCode: string): string | null {
    const fabric = this.workOrder.value!.fabrics.find((f) => f.fabricCode === fabricCode);
    const yarnDetails = fabric?.fabric?.greige.yarnDetails;
    if (!yarnDetails || yarnDetails.length === 0) {
      return null;
    }
    return yarnDetails.map((yarnDetail) => yarnDetail.loopLength).join(', ');
  }

  getYarnPercentage(yarnCode: string, fabric: Fabric): number {
    // Sum all usePercentages for yarns with the same yarnCode
    const totalPercentage = fabric.greige.yarnDetails
      .filter((yarn) => yarn.yarnCode === yarnCode)
      .reduce((sum, yarn) => sum + (yarn.usePercentage ?? 0), 0);
    return totalPercentage;
  }

  fetchFabricDescription(fabricCode: string): string {
    const { fabric } = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabricCode) ?? {};
    return fabric?.longDescription ?? '';
  }

  fetchGreigeCode(fabricCode: string): string {
    const { fabric } = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabricCode) ?? {};
    return fabric?.greige.greigeCode ?? '';
  }

  fetchGreigeDescription(fabricCode: string): string {
    const { fabric } = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabricCode) ?? {};
    return fabric?.greige.longDescription ?? '';
  }

  fetchGreigeNeedleCount(fabricCode: string): number {
    const fabric = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabricCode)?.fabric;
    if (!fabric) {
      throw new Error('Fabric not found in work order');
    }

    const knittingMachineDetails = fabric.greige.machineInformation?.knittingMachineDetailsSnapshot;
    if (!knittingMachineDetails) {
      throw new Error('Knitting machine details not found for the fabric greige');
    }

    const cylinderNeedles = knittingMachineDetails.cylinderNeedles ?? 0;
    const dialNeedles = knittingMachineDetails.dialNeedles ?? 0;

    return cylinderNeedles + dialNeedles;
  }

  fetchFabricTubularOrOpenWith(fabricCode: string): string {
    const fabric = this.workOrder.value?.fabrics.find((f) => f.fabricCode === fabricCode)?.fabric;
    if (!fabric) {
      throw new Error('Fabric not found in work order');
    }

    const enumValue = this.fabricFormEnums[fabric.fabricForm!];

    return enumValue ? enumValue : ' ';
  }

  //#endregion

  //#region Item master helpers
  public getItemMasters(command: MatchingItemMasterCommand[], workOrderReference: string, fabricCode: string): void {
    const yarnCodes = command.map((c) => c.yarnCode).filter((code) => code !== undefined && code !== null);
    this.yarnProxyService.getItemMastersByYarnCode(command).subscribe({
      next: (response) => {
        if (!_.isNil(response.data) && !_.isEmpty(response.data)) {
          yarnCodes.forEach((yc) => {
            const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
            this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = response.data![yc];
          });
        } else {
          yarnCodes.forEach((yc) => {
            const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
            this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists
          });
        }
      },
      error: () => {
        yarnCodes.forEach((yc) => {
          const yarnUniqueKey = this.buildUniqueKey(yc, workOrderReference, fabricCode);
          this.yarnUniqueKeyAndItemMaster[yarnUniqueKey] = null; // Ensure it exists even if error occurs
          console.error(`Error fetching item master for yarn code: ${yc}`);
        });
      },
    });
  }

  getYarnCodeItemMasterCode(uniqueKey: string): string {
    const itemMaster = this.yarnUniqueKeyAndItemMaster[uniqueKey];
    if (itemMaster) {
      return itemMaster.itemCode;
    }
    return 'Not Found';
  }

  buildUniqueKey(yarnCode: string, workOrderReference: string, fabricCode: string): string {
    return `${yarnCode}-${workOrderReference}-${fabricCode}`;
  }

  getManufactureAndLotsData(rollCombo: KnittingMachineRolls): { lotItem: string; provider: string }[] {
    const fabricCode = rollCombo.fabric.fabricCode;

    return this.originalKnittingOrder!.fabricQuantityDetails.filter((fqd) => fqd.fabricCode === fabricCode).flatMap((fqd) =>
      fqd.yarnAndProviderInfos!.map((x) => ({
        lotItem: x.providerLotNumber!,
        provider: x.provider!.name,
      })),
    );
  }

  //#endregion

  //#region Actions
  saveKnittingOrder(navigateAway = false): void {
    this.invalidInputs.set(false);
    if (this.isLoading() > 0) {
      console.warn('Still loading data, please wait');
      return;
    }

    const location = this.location.value;
    if (!location) {
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.selectLocation'));
      return;
    }

    if (this.knittingOrderForm.invalid) {
      const invalidFields = this.getInvalidControls(this.knittingOrderForm);
      console.warn('Invalid fields:', invalidFields);
      this.invalidInputs.set(true);
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.fillRequiredFields'), 5000);
      return;
    }

    if (this.hasInvalidFabricQuantities()) {
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.invalidFabricQuantity'), 5000);
      return;
    }

    // Skip production card validation for Greige work orders (both types)
    const isGreigeWorkOrder = this.isAnyGreigeOrder;
    if (!isGreigeWorkOrder && this.productionCardsEntriesFormArray.controls.length == 0) {
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.addProductionCard'), 5000);
      return;
    }
    if (this.fabricsQuantityDetails.controls.length === 0) {
      GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.addFabric'), 5000);
      return;
    }
    const knittingOrder: KnittingOrder = {
      status: this.status.value!,
      id: this.originalKnittingOrder?.id || undefined,
      locationCode: location,
      knittingOrderNumber: this.originalKnittingOrder?.knittingOrderNumber || undefined,
      workOrderReference: this.workOrder.value?.id,
      workOrderNumber: this.workOrder.value?.orderNumber,
      notes: this.notes,
      fabricQuantityDetails: this.fabricsQuantityDetails.controls.map((fabricControl) => ({
        fabricCode: fabricControl.value.fabric!.fabricCode,
        quantity: fabricControl.value.quantity!,
        unitOfMeasure: fabricControl.value.unitOfMeasure!,
        productionCardReference:
          typeof fabricControl.value.productionCardReference === 'object' && fabricControl.value.productionCardReference !== null
            ? fabricControl.value.productionCardReference.id
            : fabricControl.value.productionCardReference || undefined,
        variantCode: fabricControl.value.variantCode!,
        yarnAndProviderInfos: fabricControl.controls.yarnAndProviderInfos.controls.map((yarnControl) => {
          const raw = yarnControl.getRawValue();
          return {
            yarnCode: raw.yarn!.code,
            providerReference: raw.provider?.id || undefined,
            providerLotNumber: raw.providerLotNumber || undefined,
          };
        }),
      })),
      productionCardReferences:
        this.productionCardsFormArray.controls.map((pc) => pc?.value.id).filter((id): id is string => id !== null && id !== undefined) ??
        [],
    };

    if (!_.isNil(this.originalKnittingOrder)) {
      this.incrementLoading();
      this.knittingOrderProxyService
        .updateKnittingOrder(knittingOrder)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe((response) => {
          if (response.success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('CommonMessages.Success'),
              detail: this.translateService.instant('knittingOrderDetails_Messages.KnittingUpdated'),
              key: 'br',
            });
            if (navigateAway) {
              setTimeout(() => {
                this.router.navigate(['/knitting-orders']);
              }, 2000);
            }
          }
        });
    } else {
      this.incrementLoading();
      this.knittingOrderProxyService
        .createKnittingOrder(knittingOrder)
        .pipe(finalize(() => this.decrementLoading()))
        .subscribe((results) => {
          if (!results.data) {
            GlobalMessageUtil.error('Error', this.translateService.instant('knittingOrderDetails_Messages.knittingFailed'), 5000);
            return;
          }
          if (results.success && results.data) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('CommonMessages.Success'),
              detail: this.translateService.instant('knittingOrderDetails_Messages.KnittingCreated'),
              key: 'br',
            });
            setTimeout(() => {
              this.router.navigate([], {
                relativeTo: this.activatedRoute,
                queryParams: { id: results.data?.id },
                queryParamsHandling: 'merge', // keeps existing query params
              });
            }, 2000);
          }
        });
    }
  }

  private updateKnittingOrderStatus(id: string, newStatus: number): void {
    this.incrementLoading();
    this.knittingOrderProxyService
      .updateKnittingOrderStatus(id, newStatus)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.messageService.add({
              severity: 'success',
              summary: this.translateService.instant('CommonMessages.Success'),
              detail: this.translateService.instant('Status updated successfully'),
              key: 'br',
            });
            if (this.originalKnittingOrder) {
              this.originalKnittingOrder.status = newStatus;
            }
          }
        },
        error: () => {
          this.status.setValue(this.originalKnittingOrder?.status ?? 0, { emitEvent: false });
        },
      });
  }

  cancel(): void {
    this.router.navigate(['/knitting-orders']);
  }

  viewerMode(disable = false) {
    if (disable) {
      this.knittingOrderForm.enable({ emitEvent: false });
      this.machineProgrammingToolFormGroup.enable({ emitEvent: false });
      this.addingNewComboForm.enable({ emitEvent: false });

      // Activar arrays
      this.productionCardsFormArray.enable({ emitEvent: false });
      this.productionCardsEntriesFormArray.enable({ emitEvent: false });
      this.fabricsQuantityDetails.enable({ emitEvent: false });

      // Activar controles sueltos
      this.productionCardsControl.enable({ emitEvent: false });
      this.selectedFabricCodeControl.enable({ emitEvent: false });
      this.workOrderFabricSearch.enable({ emitEvent: false });
      return;
    } else {
      this.knittingOrderForm.disable({ emitEvent: false });
      this.machineProgrammingToolFormGroup.disable({ emitEvent: false });
      this.addingNewComboForm.disable({ emitEvent: false });

      // Desactivar arrays
      this.productionCardsFormArray.disable({ emitEvent: false });
      this.productionCardsEntriesFormArray.disable({ emitEvent: false });
      this.fabricsQuantityDetails.disable({ emitEvent: false });

      // Desactivar controles sueltos
      this.productionCardsControl.disable({ emitEvent: false });
      this.selectedFabricCodeControl.disable({ emitEvent: false });
      this.workOrderFabricSearch.disable({ emitEvent: false });
    }
  }

  private canEdit() {
    if (AuthService.hasRole(KnittingOrderRoles.KnittingOrderViewer)) {
      this.isViewer.set(true);
      this.viewerMode();
    }
    if (AuthService.hasRole(KnittingOrderRoles.KnittingOrderAllEditor)) {
      this.isEditor.set(true);
      this.isViewer.set(false);
      this.viewerMode(true);
      this.applyWeighedRollsLock();
    }
  }

  //#endregion

  //#region Inter-domain links
  createNewRequisition(): void {
    if (_.isNil(this.originalKnittingOrder)) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: this.translateService.instant('knittingOrderDetails_Messages.saveKnittingBefore'),
        key: 'br',
      });
      return;
    }

    const baseUrl = environment.domainUrls.inventoryUrl;
    const path = 'yarn-requisition-details';
    const queryParams = {
      addNewByKnittingOrderReference: this.originalKnittingOrder.id,
      token: this.userAuthenticationService.getStoredJwtToken(),
    };

    const finalBase = baseUrl.endsWith('/') ? baseUrl : baseUrl + '/';
    const url = new URL(finalBase + path);

    Object.entries(queryParams).forEach(([key, value]) => {
      url.searchParams.set(key, value!);
    });

    window.open(url.toString(), '_blank');
  }

  //#endregion

  //#region KM Instructions printing
  get printSelectedKmInstructionsLabel(): string {
    const count = this.selectedCombos.length;
    const translation = this.translateService.instant('Print_Selected_Km_Instructions', { count });
    return translation === 'Print_Selected_Km_Instructions' ? `Print ${count} KM Instructions` : translation;
  }

  printKmInstructions(rollCombo: KnittingMachineRolls): void {
    this.printKmInstructionsBatch([rollCombo]);
  }

  printSelectedKmInstructions(): void {
    if (_.isEmpty(this.selectedCombos)) {
      GlobalMessageUtil.warn('No hay maquinas seleccionadas', 'Selecciona al menos una fila para imprimir sus KM Instructions');
      return;
    }
    this.printKmInstructionsBatch(this.selectedCombos);
  }

  private printKmInstructionsBatch(combos: KnittingMachineRolls[]): void {
    const command: KmInstructionsCommand = {
      knittingOrderId: this.originalKnittingOrder!.id!,
      combos: combos.map((combo) => ({
        assetId: combo.asset.assetId,
        fabricCode: combo.fabric.fabricCode,
        variantCode: combo.variantCode,
      })),
    };

    this.incrementLoading();
    this.kmInstructionsProxyService
      .generateBatch(command)
      .pipe(finalize(() => this.decrementLoading()))
      .subscribe({
        next: (blob) => this.openPdfForPrint(blob),
        error: () => {
          /* handled globally by ErrorInterceptor */
        },
      });
  }

  private openPdfForPrint(response: Blob): void {
    const blob = new Blob([response], { type: 'application/pdf' });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.left = '-9999px';
    iframe.style.top = '-9999px';
    iframe.style.width = '0px';
    iframe.style.height = '0px';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    iframe.onload = () => {
      setTimeout(() => {
        if (iframe.contentWindow) {
          iframe.contentWindow.focus();
          iframe.contentWindow.print();
        }
      }, 500);
    };
    iframe.src = url;
  }

  //#endregion

  //#region Modals & notes
  viewFabricDetailsModal(fabricCode: string): void {
    const fabric = this.workOrder.value?.fabrics.find((fabric) => fabric.fabric.fabricCode === fabricCode);

    this.modalService.open(FabricInformationModalComponent, { modalClass: 'modal_small' }, { fabric: fabric?.fabric });
  }

  viewSiblingKnittingOrderModal(): void {
    this.modalService.open(RelationKnittingsModalComponent, { modalClass: 'modal_small' }, { knittings: this.siblingKnittingOrders });
  }

  onViewMore(): void {
    this.openNotesModal();
  }

  openNotesModal(): void {
    const modalData = {
      notes: this.notes,
      userLite: this.userLite,
      viewOnly: false,
    };

    this.modalService.open(NotesSidebarComponent, {}, modalData).subscribe({
      next: (result) => {
        if (result && result.action === 'noteAdded') {
          this.notes = result.data.notes;
        }
      },
      error: (error) => {
        console.error('Error handling notes modal result:', error);
      },
    });
    setTimeout(() => {
      const modalElement = document.querySelector('.panel.modal') as HTMLElement;
      if (modalElement) modalElement.style.maxWidth = '50%';
      modalElement.style.width = '50%';
    }, 100);
  }

  //#endregion
}
